import React, { useEffect, useRef, useCallback } from 'react';

type UseIdleLogoutOptions = {
  timeout: number; // ms
  onLogout: () => void;
  storageKey?: string;
};

export function useIdleLogout({
  timeout,
  onLogout,
  storageKey = 'lastActivity',
}: UseIdleLogoutOptions) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearExistingTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const logout = useCallback(() => {
    clearExistingTimer();
    onLogout();
  }, [onLogout]);

  const scheduleLogout = useCallback(() => {
    clearExistingTimer();
    timerRef.current = setTimeout(logout, timeout);
  }, [logout, timeout]);

  const updateLastActivity = useCallback(() => {
    const now = Date.now();
    localStorage.setItem(storageKey, now.toString());
    scheduleLogout();
  }, [scheduleLogout, storageKey]);

  const checkIdleTime = useCallback(() => {
    const lastActivity = localStorage.getItem(storageKey);
    if (!lastActivity) return;

    const diff = Date.now() - Number(lastActivity);

    if (diff > timeout) {
      logout();
    } else {
      scheduleLogout();
    }
  }, [logout, scheduleLogout, storageKey, timeout]);

  useEffect(() => {
    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];

    activityEvents.forEach((event) =>
      window.addEventListener(event, updateLastActivity, true),
    );

    window.addEventListener('focus', checkIdleTime);
    document.addEventListener('visibilitychange', checkIdleTime);
    window.addEventListener('storage', checkIdleTime);

    checkIdleTime();

    return () => {
      clearExistingTimer();

      activityEvents.forEach((event) =>
        window.removeEventListener(event, updateLastActivity, true),
      );

      window.removeEventListener('focus', checkIdleTime);
      document.removeEventListener('visibilitychange', checkIdleTime);
      window.removeEventListener('storage', checkIdleTime);
    };
  }, [checkIdleTime, updateLastActivity]);
}
